import { DndContext, DragOverlay } from '@dnd-kit/core';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import defaultTimeZone from '@/constants/defaultTimeZone';
import useIsSmallScreen from '@/hooks/useIsSmallScreen';
import { cn } from '@/ui/lib/utils';

import AgendaView from './components/AgendaView';
import CalendarHeader from './components/CalendarHeader';
import DayView from './components/DayView';
import EventDetailsModal from './components/EventDetailsModal';
import MonthView from './components/MonthView';
import WeekView from './components/WeekView';
import { useCalendarDrag } from './hooks/useCalendarDrag';
import type { BigCalendarProps, CalendarEvent, CalendarView } from './types';
import { parseTimeToMinutes } from './utils/dateGrid';
import { getEventColor } from './utils/eventColor';

const PX_PER_MINUTE = 1;
const SMALL_SCREEN_VIEWS: CalendarView[] = ['day', 'agenda'];
const ALL_VIEWS: CalendarView[] = ['day', 'week', 'month', 'agenda'];

function BigCalendar({
  events,
  view,
  onViewChange,
  date,
  onNavigate,
  timeZone = defaultTimeZone,
  isDateHighlighted,
  minTime = '00:00',
  maxTime = '24:00',
  snapToMinutes = 15,
  onEventClick,
  onEventChange,
  onSlotClick,
  renderEventContent,
  renderEventPopover,
  className
}: BigCalendarProps) {
  const isSmallScreen = useIsSmallScreen();
  const availableViews = isSmallScreen ? SMALL_SCREEN_VIEWS : ALL_VIEWS;

  useEffect(() => {
    if (isSmallScreen && !availableViews.includes(view)) {
      onViewChange('day');
    }
  }, [isSmallScreen, availableViews, view, onViewChange]);

  const minMinutes = useMemo(() => parseTimeToMinutes(minTime), [minTime]);
  const maxMinutes = useMemo(() => parseTimeToMinutes(maxTime), [maxTime]);

  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const drillDownToDay = useCallback(
    (day: Date) => {
      onNavigate(day);
      onViewChange('day');
    },
    [onNavigate, onViewChange]
  );

  const { sensors, activeEvent, activeSize, handleDragStart, handleDragEnd, handleDragCancel } =
    useCalendarDrag({
      timeZone,
      pxPerMinute: PX_PER_MINUTE,
      snapToMinutes,
      onEventChange
    });

  const handleEventClick = useCallback(
    (event: CalendarEvent) => {
      onEventClick?.(event);
      setSelectedEvent(event);
    },
    [onEventClick]
  );

  const sharedProps = useMemo(
    () => ({
      date,
      events,
      timeZone,
      isDateHighlighted,
      onEventClick: handleEventClick,
      onSlotClick,
      renderEventContent,
      renderEventPopover
    }),
    [
      date,
      events,
      timeZone,
      isDateHighlighted,
      handleEventClick,
      onSlotClick,
      renderEventContent,
      renderEventPopover
    ]
  );

  return (
    <div className={cn('flex flex-col', className)}>
      <CalendarHeader
        view={view}
        onViewChange={onViewChange}
        availableViews={availableViews}
        date={date}
        onNavigate={onNavigate}
        timeZone={timeZone}
      />
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {view === 'day' && (
            <DayView
              {...sharedProps}
              minMinutes={minMinutes}
              maxMinutes={maxMinutes}
              pxPerMinute={PX_PER_MINUTE}
              snapToMinutes={snapToMinutes}
            />
          )}
          {view === 'week' && (
            <WeekView
              {...sharedProps}
              minMinutes={minMinutes}
              maxMinutes={maxMinutes}
              pxPerMinute={PX_PER_MINUTE}
              snapToMinutes={snapToMinutes}
              onDayHeaderClick={drillDownToDay}
            />
          )}
          {view === 'month' && <MonthView {...sharedProps} onDayNumberClick={drillDownToDay} />}
          {view === 'agenda' && <AgendaView {...sharedProps} />}
        </div>
        {createPortal(
          <DragOverlay>
            {activeEvent && (
              <div
                className="overflow-hidden rounded-md px-1.5 py-0.5 text-xs text-white shadow-lg ring-1 ring-black/10"
                style={{
                  backgroundColor: getEventColor(activeEvent),
                  width: activeSize?.width,
                  height: activeSize?.height
                }}>
                <span className="block truncate font-medium">{activeEvent.title}</span>
              </div>
            )}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
      <EventDetailsModal
        event={selectedEvent}
        timeZone={timeZone}
        onClose={() => setSelectedEvent(null)}
      />
    </div>
  );
}

export default memo(BigCalendar);
